import { StyledForm } from '@Components/shared/Form.styles';
import { useGlobalization } from '@Translations';
import {
	DateFormatOptions,
	QuoteFormatOptions,
	ServerTypeOptions,
	TagFieldFormatOptions,
	TimestampConversionOptions,
} from '@Types/systemConnections.types';
import {
	Autocomplete,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
} from '@mui/material';
import React from 'react';
import { SystemConnectionType } from './NewSystemConnectionForm';
import { VisiblePasswordField } from './VisiblePassword';

export interface OdbcProps {
	updateFormData: (p: Record<string, string>) => void;
	formData: Record<string, string>;
	formErrors: Record<string, string>;
	systemConnectionTypes: SystemConnectionType[];
	handleConnectionTypeChange: (event: SelectChangeEvent<string>) => void;
	handleServerTypeChange: (
		_event: React.ChangeEvent<unknown>,
		newValue: string | null,
	) => void;
	connectionStringTextChanged: (event: React.FormEvent) => void;
	textFieldChanged: (event: React.FormEvent) => void;
	numberFieldChanged: (event: React.FormEvent) => void;
	tzOptions: { key: string; value: string }[];
	tzDefault: { key: string; value: string } | undefined;
	handleSelectChange: (event: SelectChangeEvent<string>) => void;
}

export const OdbcFields = (props: OdbcProps) => {
	const { t } = useGlobalization();

	const changeDatabaseTimezoneAutocompleteHandler =
		(name: string) =>
		(
			event: React.ChangeEvent<unknown>,
			newValue: { key: string; value: string } | null,
		) => {
			props.updateFormData({
				[name]: newValue?.key || '',
			});
		};

	const ignoreScroll = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
	};

	return (
		<StyledForm autoComplete="off">
			<Grid container spacing={3}>
				<Grid item xs={6}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						required={true}
						label={t('Connection Name')}
						variant="outlined"
						name={'connectionName'}
						value={props.formData?.connectionName ?? ''}
						onChange={props.textFieldChanged}
						error={!!props.formErrors?.connectionName}
						helperText={props.formErrors?.connectionName}
					/>
				</Grid>

				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						error={!!props.formErrors?.connectionType}>
						<InputLabel>{t('Connection Type *')}</InputLabel>
						<Select
							name="connectionType"
							required={true}
							value={props.formData?.connectionType ?? 'ODBC'}
							onChange={props.handleConnectionTypeChange}
							error={!!props.formErrors?.connectionType}
							label={t('Connection Type')}>
							{props.systemConnectionTypes.map(
								({
									systemConnectionTypeName,
									systemConnectionTypeId,
								}) => (
									<MenuItem
										key={systemConnectionTypeId}
										value={systemConnectionTypeId}>
										{systemConnectionTypeName}
									</MenuItem>
								),
							)}
						</Select>
						{!!props.formErrors?.connectionType && (
							<FormHelperText>
								{props.formErrors?.connectionType}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid item xs={4}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Server Address')}
						required={true}
						variant="outlined"
						name={'serverAddress'}
						value={props.formData?.serverAddress ?? ''}
						onChange={props.textFieldChanged}
						error={!!props.formErrors?.serverAddress}
						helperText={props.formErrors?.serverAddress}
					/>
				</Grid>

				<Grid item sm={4}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Port')}
						required={true}
						variant="outlined"
						name={'port'}
						type={'number'}
						onWheel={ignoreScroll}
						InputProps={{ inputProps: { min: 0 } }}
						value={props.formData?.port ?? ''}
						onChange={props.numberFieldChanged}
						error={!!props.formErrors?.port}
						helperText={props.formErrors?.port}
					/>
				</Grid>

				<Grid item sm={4}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Database')}
						required={false}
						variant="outlined"
						name={'database'}
						value={props.formData?.database ?? ''}
						onChange={props.textFieldChanged}
						error={!!props.formErrors?.database}
						helperText={props.formErrors?.database}
					/>
				</Grid>

				<Grid item xs={4}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Username')}
						required={true}
						variant="outlined"
						name={'username'}
						value={props.formData?.username ?? ''}
						onChange={props.textFieldChanged}
						error={!!props.formErrors?.username}
						helperText={props.formErrors?.username}
					/>
				</Grid>

				<Grid item xs={4}>
					<VisiblePasswordField
						value={props.formData?.password}
						passwordError={props.formErrors?.password}
						textFieldChanged={props.textFieldChanged}
					/>
				</Grid>

				<Grid item xs={4}>
					<Autocomplete
						id="serverType-autocomplete"
						value={props.formData?.serverType || ''}
						onChange={props.handleServerTypeChange}
						color={'primary'}
						options={ServerTypeOptions}
						defaultValue={ServerTypeOptions[0]}
						fullWidth
						renderInput={params => (
							<TextField
								{...params}
								name={'serverType'}
								label={t('Driver')}
								required={true}
								variant="outlined"
								margin={'normal'}
								error={!!props.formErrors?.serverType}
								helperText={props.formErrors?.serverType}
							/>
						)}
						onInputChange={props.textFieldChanged}
					/>
				</Grid>

				<Grid item xs={12}>
					<TextField
						color={'primary'}
						fullWidth
						multiline
						rows={4}
						margin={'normal'}
						label={t('Connection String')}
						required={true}
						variant="outlined"
						value={props.formData?.connectionString ?? ''}
						name={'connectionString'}
						onChange={props.connectionStringTextChanged}
						error={!!props.formErrors?.connectionString}
						helperText={props.formErrors?.connectionString}
						inputProps={{ sx: { fontFamily: 'Monospace' } }}
					/>
				</Grid>

				<Grid item xs={6}>
					<Autocomplete
						id="database-timezone-id"
						options={props.tzOptions}
						getOptionLabel={option => option.value}
						onChange={changeDatabaseTimezoneAutocompleteHandler(
							'databaseTimezone',
						)}
						color={'primary'}
						defaultValue={props.tzDefault}
						isOptionEqualToValue={(option, value) =>
							option.value === value.value
						}
						value={{
							key: props.formData?.databaseTimezone,
							value: props.formData?.databaseTimezone?.replace(
								/_/g,
								' ',
							),
						}}
						fullWidth
						renderInput={params => (
							<TextField
								{...params}
								name={'databaseTimezone'}
								label={t('Database Timezone [IANA]')}
								required={true}
								variant="outlined"
								margin={'normal'}
								error={!!props.formErrors?.databaseTimezone}
								helperText={props.formErrors?.databaseTimezone}
							/>
						)}
						onInputChange={props.textFieldChanged}
					/>
				</Grid>

				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						error={!!props.formErrors?.caseSensitiveSensorId}>
						<InputLabel>
							{t('Case Sensitive SensorID *')}
						</InputLabel>
						<Select
							name={'caseSensitiveSensorId'}
							required={true}
							label={t('Case Sensitive SensorID')}
							value={`${props.formData?.caseSensitiveSensorId}`}
							onChange={props.handleSelectChange}
							error={!!props.formErrors?.caseSensitiveSensorId}>
							<MenuItem value={'true'}>{t('True')}</MenuItem>
							<MenuItem value={'false'}>{t('False')}</MenuItem>
						</Select>
						{!!props.formErrors?.caseSensitiveSensorId && (
							<FormHelperText>
								{props.formErrors?.caseSensitiveSensorId}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid item xs={6}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Sample Frequency/Duration [Seconds]')}
						required={true}
						variant="outlined"
						name={'sampleDurationSeconds'}
						type={'number'}
						onWheel={ignoreScroll}
						InputProps={{ inputProps: { min: 0 } }}
						value={props.formData?.sampleDurationSeconds ?? ''}
						onChange={props.numberFieldChanged}
						error={!!props.formErrors?.sampleDurationSeconds}
						helperText={props.formErrors?.sampleDurationSeconds}
					/>
				</Grid>

				<Grid item xs={6}>
					<TextField
						required={true}
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Query Time Lag [Minutes]')}
						variant="outlined"
						name={'queryLagMinutes'}
						type={'number'}
						onWheel={ignoreScroll}
						InputProps={{ inputProps: { min: 0 } }}
						value={props.formData?.queryLagMinutes ?? ''}
						onChange={props.numberFieldChanged}
						error={!!props.formErrors?.queryLagMinutes}
						helperText={props.formErrors?.queryLagMinutes}
					/>
				</Grid>

				<Grid item xs={6}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Query Group Size')}
						required={true}
						variant="outlined"
						name={'queryGroupSize'}
						type={'number'}
						onWheel={ignoreScroll}
						InputProps={{ inputProps: { min: 1 } }}
						value={props.formData?.queryGroupSize ?? ''}
						onChange={props.numberFieldChanged}
						error={!!props.formErrors?.queryGroupSize}
						helperText={props.formErrors?.queryGroupSize}
					/>
				</Grid>

				<Grid item xs={6}>
					<TextField
						required={true}
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Simulation Subtraction [Days]')}
						variant="outlined"
						name={'simulationSubtractionDays'}
						type={'number'}
						onWheel={ignoreScroll}
						InputProps={{ inputProps: { min: 0 } }}
						value={props.formData?.simulationSubtractionDays ?? ''}
						onChange={props.numberFieldChanged}
						error={!!props.formErrors?.simulationSubtractionDays}
						helperText={props.formErrors?.simulationSubtractionDays}
					/>
				</Grid>

				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						error={!!props.formErrors?.dateTimeFormat}>
						<InputLabel>{t('Date Format')}</InputLabel>
						<Select
							name="dateTimeFormat"
							required={false}
							value={props.formData?.dateTimeFormat}
							onChange={props.handleSelectChange}
							error={!!props.formErrors?.dateTimeFormat}
							label={t('Date Format')}>
							{Object.entries(DateFormatOptions).map(
								([key, { label, value }]) => (
									<MenuItem key={key} value={value}>
										{label}
									</MenuItem>
								),
							)}
						</Select>
						{!!props.formErrors?.dateTimeFormat && (
							<FormHelperText>
								{props.formErrors?.dateTimeFormat}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						error={!!props.formErrors?.quoteFormat}>
						<InputLabel>{t('Quote Format')}</InputLabel>
						<Select
							name="quoteFormat"
							required={false}
							value={props.formData?.quoteFormat}
							onChange={props.handleSelectChange}
							error={!!props.formErrors?.quoteFormat}
							label={t('Quote Format')}>
							{Object.entries(QuoteFormatOptions).map(
								([key, { label, value }]) => (
									<MenuItem key={key} value={value}>
										{label}
									</MenuItem>
								),
							)}
						</Select>
						{!!props.formErrors?.quoteFormat && (
							<FormHelperText>
								{props.formErrors?.quoteFormat}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						error={!!props.formErrors?.timestampConversion}>
						<InputLabel>{t('Timestamp Conversion')}</InputLabel>
						<Select
							name="timestampConversion"
							required={false}
							value={props.formData?.timestampConversion}
							onChange={props.handleSelectChange}
							error={!!props.formErrors?.timestampConversion}
							label={t('Timestamp Conversion')}>
							{Object.entries(TimestampConversionOptions).map(
								([key, { label, value }]) => (
									<MenuItem key={key} value={value}>
										{label}
									</MenuItem>
								),
							)}
						</Select>
						{!!props.formErrors?.timestampConversion && (
							<FormHelperText>
								{props.formErrors?.timestampConversion}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						error={!!props.formErrors?.tagFormat}>
						<InputLabel>{t('Tag Field Format')}</InputLabel>
						<Select
							name="tagFormat"
							required={false}
							value={props.formData?.tagFormat}
							onChange={props.handleSelectChange}
							error={!!props.formErrors?.tagFormat}
							label={t('Connection Type')}>
							{Object.entries(TagFieldFormatOptions).map(
								([key, { label, value }]) => (
									<MenuItem key={key} value={value}>
										{label}
									</MenuItem>
								),
							)}
						</Select>
						{!!props.formErrors?.tagFormat && (
							<FormHelperText>
								{props.formErrors?.tagFormat}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>
			</Grid>
		</StyledForm>
	);
};
